import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  getDoc,
  setDoc,
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  getAuth
} from 'firebase/auth';
import { db } from '../firebase';
import { 
  generateInvitationToken, 
  generateSecurePassword,
  validatePasswordStrength 
} from './passwordService';

const USERS_COLLECTION = 'users';
const INVITATIONS_COLLECTION = 'userInvitations';

// User roles
export const USER_ROLES = {
  ADMIN: 'admin',
  STAFF: 'staff'
};

// Permissions for each role
export const PERMISSIONS = {
  [USER_ROLES.ADMIN]: {
    quotations: ['create', 'read', 'update', 'delete', 'export'],
    customers: ['create', 'read', 'update', 'delete'],
    catalog: ['create', 'read', 'update', 'delete'],
    users: ['create', 'read', 'update', 'delete'],
    settings: ['read', 'update'],
    reports: ['view_all', 'export']
  },
  [USER_ROLES.STAFF]: {
    quotations: ['create', 'read', 'update', 'delete', 'export'],
    customers: ['create', 'read', 'update', 'delete'],
    catalog: ['create', 'read', 'update', 'delete'],
    reports: ['view_own']
  }
};

/**
 * Initialize the current user's role data in Firestore
 * This should be called when a user first logs in
 */
export const initializeUserRole = async (userId, email, role = USER_ROLES.ADMIN) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      const userData = {
        uid: userId,
        email: email,
        role: role,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(userRef, userData);
      console.log('User role initialized:', userData);
      return userData;
    }
    
    return userDoc.data();
  } catch (error) {
    console.error('Error initializing user role:', error);
    throw error;
  }
};

/**
 * Get user role and permissions
 */
export const getUserRole = async (userId) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data();
    }
    
    // If user doesn't exist in users collection, assume admin (for existing users)
    return {
      role: USER_ROLES.ADMIN,
      permissions: PERMISSIONS[USER_ROLES.ADMIN]
    };
  } catch (error) {
    console.error('Error getting user role:', error);
    throw error;
  }
};

/**
 * Check if user has specific permission
 */
export const hasPermission = (userRole, module, action) => {
  const permissions = PERMISSIONS[userRole];
  if (!permissions || !permissions[module]) {
    return false;
  }
  return permissions[module].includes(action);
};

/**
 * Create a secure user invitation (Admin only)
 * This creates an invitation that the person can use to register
 */
export const createUserInvitation = async (userData, createdByUserId) => {
  try {
    // Validate password strength if provided
    if (userData.password) {
      const passwordValidation = validatePasswordStrength(userData.password);
      if (!passwordValidation.isValid) {
        throw new Error(`Password validation failed: ${passwordValidation.feedback.join(', ')}`);
      }
    }
    
    // Generate secure invitation token
    const invitationToken = generateInvitationToken();
    
    // Create user invitation document in Firestore
    const userInvitationData = {
      id: invitationToken,
      email: userData.email,
      role: userData.role || USER_ROLES.STAFF,
      isActive: true,
      createdBy: createdByUserId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'invited', // invited, registered, expired
      invitationCode: Math.random().toString(36).substr(2, 8).toUpperCase(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
    };
    
    await setDoc(doc(db, INVITATIONS_COLLECTION, invitationToken), userInvitationData);
    
    return { 
      id: invitationToken, 
      ...userInvitationData,
      message: `Invitation created! Share these details with the user:
Email: ${userData.email}
Invitation Code: ${userInvitationData.invitationCode}
They can register at the login page using this code.
Invitation expires in 7 days.`
    };
  } catch (error) {
    console.error('Error creating user invitation:', error);
    throw error;
  }
};

/**
 * Register a user with invitation code
 */
export const registerWithInvitation = async (email, password, invitationCode) => {
  try {
    // Validate password strength
    const passwordValidation = validatePasswordStrength(password);
    if (!passwordValidation.isValid) {
      throw new Error(`Password validation failed: ${passwordValidation.feedback.join(', ')}`);
    }
    
    // Find invitation
    const invitationsRef = collection(db, INVITATIONS_COLLECTION);
    const q = query(
      invitationsRef, 
      where('email', '==', email),
      where('invitationCode', '==', invitationCode),
      where('status', '==', 'invited')
    );
    
    const invitationSnapshot = await getDocs(q);
    
    if (invitationSnapshot.empty) {
      throw new Error('Invalid invitation code or email');
    }
    
    const invitationDoc = invitationSnapshot.docs[0];
    const invitationData = invitationDoc.data();
    
    // Check if invitation has expired
    if (invitationData.expiresAt && invitationData.expiresAt.toDate() < new Date()) {
      throw new Error('Invitation has expired. Please request a new invitation.');
    }
    
    // Create Firebase Auth user
    const auth = getAuth();
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const newUser = userCredential.user;
    
    // Create user document in Firestore (no password stored)
    const userDocData = {
      uid: newUser.uid,
      email: email,
      role: invitationData.role,
      isActive: true,
      createdBy: invitationData.createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(doc(db, USERS_COLLECTION, newUser.uid), userDocData);
    
    // Update invitation status
    await updateDoc(doc(db, INVITATIONS_COLLECTION, invitationDoc.id), {
      status: 'registered',
      registeredAt: serverTimestamp(),
      userId: newUser.uid
    });
    
    return { success: true, user: userDocData };
  } catch (error) {
    console.error('Error registering with invitation:', error);
    throw error;
  }
};

/**
 * Get all invitations (Admin only)
 */
export const getAllInvitations = async () => {
  try {
    const invitationsRef = collection(db, INVITATIONS_COLLECTION);
    const q = query(invitationsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const invitations = [];
    snapshot.forEach((doc) => {
      invitations.push({ id: doc.id, ...doc.data() });
    });
    
    return invitations;
  } catch (error) {
    console.error('Error fetching invitations:', error);
    throw error;
  }
};

/**
 * Get all users (Admin only)
 */
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(usersRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const users = [];
    snapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    return users;
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

/**
 * Update user data (Admin only)
 */
export const updateUser = async (userId, updateData) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const dataToUpdate = {
      ...updateData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(userRef, dataToUpdate);
    return { success: true };
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * Deactivate user (Admin only)
 */
export const deactivateUser = async (userId) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      isActive: false,
      deactivatedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error deactivating user:', error);
    throw error;
  }
};

/**
 * Activate user (Admin only)
 */
export const activateUser = async (userId) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      isActive: true,
      activatedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error activating user:', error);
    throw error;
  }
};

/**
 * Remove user completely (Admin only)
 */
export const removeUser = async (userId) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await deleteDoc(userRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Delete invitation (Admin only)
 */
export const deleteInvitation = async (invitationId) => {
  try {
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    await deleteDoc(invitationRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting invitation:', error);
    throw error;
  }
};

/**
 * Create a staff account with secure invitation system (Admin only)
 * This replaces the old temporary password system
 */
export const createStaffAccount = async (userData, createdByUserId) => {
  try {
    // Generate a secure temporary password if none provided
    const tempPassword = userData.password || generateSecurePassword(12);
    
    // Validate the password
    const passwordValidation = validatePasswordStrength(tempPassword);
    if (!passwordValidation.isValid) {
      throw new Error(`Generated password validation failed: ${passwordValidation.feedback.join(', ')}`);
    }
    
    // Create the invitation instead of direct user creation
    const invitation = await createUserInvitation({
      email: userData.email,
      role: userData.role || USER_ROLES.STAFF,
      password: tempPassword
    }, createdByUserId);
    
    return {
      ...invitation,
      credentials: {
        email: userData.email,
        temporaryPassword: tempPassword
      },
      message: `Staff invitation created successfully!

Invitation Details:
Email: ${userData.email}
Invitation Code: ${invitation.invitationCode}
Temporary Password: ${tempPassword}

Instructions for staff member:
1. Go to the login page
2. Click "Have an invitation code? Register here"
3. Enter email and invitation code
4. Set a new secure password
5. Complete registration

The invitation expires in 7 days.`
    };
  } catch (error) {
    console.error('Error creating staff account:', error);
    throw error;
  }
}; 
 